// ---------------------------------------------------------------------------
// FeltonContentUniverse -- Explore audience content universe
// ---------------------------------------------------------------------------
// Multi-tab page showing content, comments, brands, and teams
// for a filtered funnel audience.
// ---------------------------------------------------------------------------

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, LayoutGrid, MessageSquare, ShoppingBag, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DataLoader } from '@/components/v11/discovery';
import { contentUniverseService } from '@/services/v11/contentUniverseService';
import type { FunnelLayerInput } from '@/services/v11/funnelService';
import type {
  ContentUniversePost,
  BrandAffinity,
  TeamLoyalty,
  ContentSortField,
  SortOrder,
} from '@/components/v11/contentUniverse/types';
import { PeopleMosaic } from '@/components/v11/contentUniverse/PeopleMosaic';
import { OverviewPanel } from '@/components/v11/contentUniverse/OverviewPanel';
import { ContentWallPanel } from '@/components/v11/contentUniverse/ContentWallPanel';
import { CommentsFeedPanel } from '@/components/v11/contentUniverse/CommentsFeedPanel';
import { BrandsTeamsPanel } from '@/components/v11/contentUniverse/BrandsTeamsPanel';

// ---------------------------------------------------------------------------
// Tab Definitions
// ---------------------------------------------------------------------------

type TabId = 'overview' | 'content' | 'comments' | 'brands';

const TABS: readonly {
  readonly id: TabId;
  readonly label: string;
  readonly icon: React.ReactNode;
}[] = [
  { id: 'overview', label: 'Overview', icon: <BarChart3 className="h-4 w-4" /> },
  { id: 'content', label: 'Content Wall', icon: <LayoutGrid className="h-4 w-4" /> },
  { id: 'comments', label: 'Comments', icon: <MessageSquare className="h-4 w-4" /> },
  { id: 'brands', label: 'Brands & Teams', icon: <ShoppingBag className="h-4 w-4" /> },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function FeltonContentUniverse() {
  const location = useLocation();
  const navigate = useNavigate();

  // Get layers from navigation state OR URL search params (new tab)
  const state = location.state as {
    layers?: (FunnelLayerInput & { dimensionLabel?: string; valueLabels?: readonly string[] })[];
    origin?: { count: number };
  } | null;

  const { layers, originCount } = useMemo(() => {
    // Prefer navigation state (same-tab navigation)
    if (state?.layers && state.layers.length > 0) {
      return { layers: state.layers, originCount: state.origin?.count ?? 0 };
    }
    // Fallback: read from URL search params (new tab via window.open)
    const searchParams = new URLSearchParams(location.search);
    const layersParam = searchParams.get('layers');
    const originParam = searchParams.get('originCount');
    if (layersParam) {
      try {
        const parsed = JSON.parse(layersParam) as (FunnelLayerInput & { dimensionLabel?: string; valueLabels?: readonly string[] })[];
        return { layers: parsed, originCount: originParam ? Number(originParam) : 0 };
      } catch {
        return {
          layers: [] as (FunnelLayerInput & {
            dimensionLabel?: string;
            valueLabels?: readonly string[];
          })[],
          originCount: 0,
        };
      }
    }
    return {
      layers: [] as (FunnelLayerInput & {
        dimensionLabel?: string;
        valueLabels?: readonly string[];
      })[],
      originCount: 0,
    };
  }, [state, location.search]);

  // Tab state
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // Content pagination/sort state
  const [contentPage, setContentPage] = useState(1);
  const [contentSort, setContentSort] = useState<ContentSortField>('audience_count');
  const [contentOrder, setContentOrder] = useState<SortOrder>('desc');
  const [allPosts, setAllPosts] = useState<ContentUniversePost[]>([]);

  // Comments summary (no pagination needed — uses summary endpoint)

  // Brands/Teams pagination state
  const [brandsPage, setBrandsPage] = useState(1);
  const [teamsPage, setTeamsPage] = useState(1);
  const [allBrands, setAllBrands] = useState<BrandAffinity[]>([]);
  const [allTeams, setAllTeams] = useState<TeamLoyalty[]>([]);

  // Has more flags
  const [hasMorePosts, setHasMorePosts] = useState(false);
  const [hasMoreBrands, setHasMoreBrands] = useState(false);
  const [hasMoreTeams, setHasMoreTeams] = useState(false);

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------

  // Overview (always loaded)
  const overviewQuery = useQuery({
    queryKey: ['content-universe-overview', layers],
    queryFn: () => contentUniverseService.getOverview(layers),
    enabled: layers.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // People (always loaded for mosaic)
  const peopleQuery = useQuery({
    queryKey: ['content-universe-people', layers],
    queryFn: () => contentUniverseService.getPeople(layers, 50),
    enabled: layers.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Content (load on tab switch)
  const contentQuery = useQuery({
    queryKey: ['content-universe-content', layers, contentPage, contentSort, contentOrder],
    queryFn: () =>
      contentUniverseService.getContent(layers, contentPage, 25, contentSort, contentOrder),
    enabled: layers.length > 0 && activeTab === 'content',
    staleTime: 5 * 60 * 1000,
  });

  // Sync allPosts from query data (works with both fresh fetches AND cache hits)
  const contentData = contentQuery.data;
  useEffect(() => {
    if (!contentData) return;
    if (contentPage === 1) {
      setAllPosts([...contentData.data]);
    } else {
      setAllPosts(prev => {
        const existingIds = new Set(prev.map((p: ContentUniversePost) => p.id));
        const newPosts = contentData.data.filter(
          (p: ContentUniversePost) => !existingIds.has(p.id)
        );
        return [...prev, ...newPosts];
      });
    }
    setHasMorePosts(contentData.meta.hasMorePages);
  }, [contentData, contentPage]);

  // Comments Summary (load on tab switch)
  const commentsSummaryQuery = useQuery({
    queryKey: ['content-universe-comments-summary', layers],
    queryFn: () => contentUniverseService.getCommentsSummary(layers),
    enabled: layers.length > 0 && activeTab === 'comments',
    staleTime: 5 * 60 * 1000,
  });

  // Brands (load on tab switch)
  const brandsQuery = useQuery({
    queryKey: ['content-universe-brands', layers, brandsPage],
    queryFn: () => contentUniverseService.getBrands(layers, brandsPage, 25),
    enabled: layers.length > 0 && activeTab === 'brands',
    staleTime: 5 * 60 * 1000,
  });

  const brandsData = brandsQuery.data;
  useEffect(() => {
    if (!brandsData) return;
    if (brandsPage === 1) {
      setAllBrands([...brandsData.data]);
    } else {
      setAllBrands(prev => {
        const existingIds = new Set(prev.map((b: BrandAffinity) => b.brand_id));
        const newBrands = brandsData.data.filter(
          (b: BrandAffinity) => !existingIds.has(b.brand_id)
        );
        return [...prev, ...newBrands];
      });
    }
    setHasMoreBrands(brandsData.meta.hasMorePages);
  }, [brandsData, brandsPage]);

  // Teams (load on tab switch)
  const teamsQuery = useQuery({
    queryKey: ['content-universe-teams', layers, teamsPage],
    queryFn: () => contentUniverseService.getTeams(layers, teamsPage, 25),
    enabled: layers.length > 0 && activeTab === 'brands',
    staleTime: 5 * 60 * 1000,
  });

  const teamsData = teamsQuery.data;
  useEffect(() => {
    if (!teamsData) return;
    if (teamsPage === 1) {
      setAllTeams([...teamsData.data]);
    } else {
      setAllTeams(prev => {
        const existingIds = new Set(prev.map((t: TeamLoyalty) => t.entity_id));
        const newTeams = teamsData.data.filter((t: TeamLoyalty) => !existingIds.has(t.entity_id));
        return [...prev, ...newTeams];
      });
    }
    setHasMoreTeams(teamsData.meta.hasMorePages);
  }, [teamsData, teamsPage]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleContentSortChange = useCallback((sort: ContentSortField, order: SortOrder) => {
    setContentSort(sort);
    setContentOrder(order);
    setContentPage(1);
    setAllPosts([]);
  }, []);

  const handleBack = useCallback(() => {
    navigate('/v11/funnel');
  }, [navigate]);

  // -------------------------------------------------------------------------
  // No layers guard
  // -------------------------------------------------------------------------

  if (layers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <p className="text-v11-text-muted mb-4">
          No funnel layers provided. Go back to the Funnel Builder.
        </p>
        <Button onClick={handleBack} className="bg-blue-500 hover:bg-blue-600">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Funnel
        </Button>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Loading state
  // -------------------------------------------------------------------------

  const isInitialLoading = overviewQuery.isLoading || peopleQuery.isLoading;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Top Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="text-v11-text-muted hover:text-v11-text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-v11-text-primary">Persona Detailed Analysis</h1>
            <p className="text-sm text-v11-text-muted">
              Exploring {overviewQuery.data?.data?.totalProfiles?.toLocaleString() ?? '...'}{' '}
              profiles
            </p>
          </div>
        </div>

        {/* Layer chips */}
        <div className="flex items-center gap-2 flex-wrap">
          {layers.map((layer, i) => (
            <span
              key={i}
              className="text-xs px-3 py-1 rounded-full bg-v11-surface border border-v11-border text-v11-text-secondary"
            >
              {layer.dimensionLabel ??
                layer.dimension.charAt(0).toUpperCase() + layer.dimension.slice(1)}
              :{' '}
              {(layer.valueLabels && layer.valueLabels.length > 0
                ? [...layer.valueLabels]
                : [...layer.values]
              ).join(', ')}
            </span>
          ))}
        </div>
      </div>

      {/* People Mosaic */}
      {peopleQuery.data?.data && (
        <PeopleMosaic
          profiles={peopleQuery.data.data}
          totalCount={overviewQuery.data?.data?.totalProfiles ?? originCount}
        />
      )}

      {/* View Toggle (button group — matches Discovery toolbar) */}
      <div className="flex items-center gap-2">
        {TABS.map(tab => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab(tab.id)}
            aria-pressed={activeTab === tab.id}
            className={cn(
              'text-xs transition',
              activeTab === tab.id
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'border-v11-border text-v11-text-secondary hover:bg-v11-surface-hover hover:text-v11-text-primary'
            )}
          >
            {tab.icon}
            <span className="hidden sm:inline">{tab.label}</span>
          </Button>
        ))}
      </div>

      {/* Tab Content */}
      {isInitialLoading ? (
        <div className="flex justify-center py-6">
          <DataLoader dataSource="funnel" variant="initial" />
        </div>
      ) : (
        <>
          {activeTab === 'overview' && overviewQuery.data?.data && (
            <OverviewPanel
              data={overviewQuery.data.data}
              layers={layers}
              people={peopleQuery.data?.data ?? []}
            />
          )}

          {activeTab === 'content' &&
            (contentQuery.isLoading && allPosts.length === 0 ? (
              <div className="flex justify-center py-6">
                <DataLoader dataSource="content" variant="initial" />
              </div>
            ) : (
              <ContentWallPanel
                posts={allPosts}
                hasMore={hasMorePosts}
                loading={contentQuery.isFetching}
                sort={contentSort}
                order={contentOrder}
                onSortChange={handleContentSortChange}
                onLoadMore={() => setContentPage(p => p + 1)}
              />
            ))}

          {activeTab === 'comments' &&
            (commentsSummaryQuery.isLoading ? (
              <div className="flex justify-center py-6">
                <DataLoader dataSource="comments" variant="initial" />
              </div>
            ) : (
              <CommentsFeedPanel
                data={commentsSummaryQuery.data?.data ?? null}
                loading={commentsSummaryQuery.isFetching}
              />
            ))}

          {activeTab === 'brands' &&
            ((brandsQuery.isLoading || teamsQuery.isLoading) &&
            allBrands.length === 0 &&
            allTeams.length === 0 ? (
              <div className="flex justify-center py-6">
                <DataLoader dataSource="brands" variant="initial" />
              </div>
            ) : (
              <BrandsTeamsPanel
                brands={allBrands}
                teams={allTeams}
                totalProfiles={overviewQuery.data?.data?.totalProfiles ?? 0}
                hasMoreBrands={hasMoreBrands}
                hasMoreTeams={hasMoreTeams}
                loadingBrands={brandsQuery.isFetching}
                loadingTeams={teamsQuery.isFetching}
                onLoadMoreBrands={() => setBrandsPage(p => p + 1)}
                onLoadMoreTeams={() => setTeamsPage(p => p + 1)}
              />
            ))}
        </>
      )}
    </div>
  );
}

export { FeltonContentUniverse };
